import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import { Loader2, ArrowLeft, AlertTriangle, FileText, Landmark, UserCircle, CalendarDays, Hash, HandCoins, Ban } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { getVoucher, listPaymentAllocations, cancelVoucher } from '@/app/services/voucherService';
import { toast } from 'sonner';
import { VoucherResponse } from '@/app/types/voucher';
import { PaymentAllocationResponse } from '@/app/types/payment';
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from '@/components/ui/badge';
import { RecordLinkIcon } from '@/app/components/RecordLinkIcon';
import { CancelPaymentModal } from '../components/CancelPaymentModal';

// Helper to format currency
const formatCurrency = (amount: number | undefined | null): string => {
  if (amount === undefined || amount === null) return 'N/A';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR', // TODO: Make dynamic
  }).format(Number(amount));
};

// Helper to format date
const formatDate = (dateString: string | Date | undefined | null): string => {
  if (!dateString) return 'N/A';
  try {
      const date = typeof dateString === 'string' ? parseISO(dateString) : dateString;
      // Adjust format as needed
      return format(date, 'dd MMM yyyy');
  } catch (error) {
    return String(dateString); // Fallback
  }
};

const PaymentsOutDetailPage: React.FC = (): JSX.Element => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const paymentId = Number(id);
  const [showCancelModal, setShowCancelModal] = useState(false);

  // Fetch Payment Voucher details
  const { data: paymentData, isLoading, isError, error } = useQuery<VoucherResponse, Error>({
    queryKey: ['voucher', paymentId],
    queryFn: () => getVoucher(paymentId),
    enabled: !!paymentId && !isNaN(paymentId),
  });

  // Fetch Payment Allocations
  const { data: allocationsData, isLoading: isLoadingAllocations } = useQuery<PaymentAllocationResponse[], Error>({
    queryKey: ['paymentAllocations', paymentId],
    queryFn: () => listPaymentAllocations(paymentId),
    enabled: !!paymentData, // Only run if payment data exists
  });

  // Cancel Payment Mutation
  const cancelPaymentMutation = useMutation({
    mutationFn: () => cancelVoucher(paymentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['voucher', paymentId] });
      queryClient.invalidateQueries({ queryKey: ['paymentsOut'] });
      queryClient.invalidateQueries({ queryKey: ['vouchers'] });
      toast.success('Payment voucher cancelled successfully');
      setShowCancelModal(false);
      // Refetch the current payment data to update the UI
      queryClient.refetchQueries({ queryKey: ['voucher', paymentId] });
    },
    onError: (error: any) => {
      const errorMsg = error.response?.data?.detail || error.message || "Failed to cancel";
      toast.error(`Failed to cancel payment voucher: ${errorMsg}`);
    },
  });

  // Handler for confirming cancellation
  const handleConfirmCancel = (): void => {
    cancelPaymentMutation.mutate();
  };

  // Display loading state
  if (isLoading) {
    return (
        <div className="container mx-auto py-6 space-y-6">
             <div className="flex items-center gap-4 mb-4">
                <Button variant="outline" size="icon" onClick={() => navigate(-1)}>
                  <ArrowLeft className="h-4 w-4" />
                </Button>
                <Skeleton className="h-8 w-48" /> {/* Skeleton for title */}
            </div>
            <Card>
                <CardHeader><Skeleton className="h-6 w-32" /></CardHeader>
                <CardContent className="space-y-4">
                    <Skeleton className="h-5 w-full" />
                    <Skeleton className="h-5 w-2/3" />
                    <Skeleton className="h-5 w-1/2" />
                </CardContent>
            </Card>
            {/* Skeleton for allocation table */}
            <Card>
                <CardHeader><Skeleton className="h-6 w-32" /></CardHeader>
                <CardContent>
                    <Skeleton className="h-32 w-full" />
                </CardContent>
            </Card>
        </div>
    );
  }

  // Display error state
  if (isError || !paymentData) {
    return (
      <div className="container mx-auto py-6 space-y-6">
         <div className="flex items-center gap-4 mb-4">
            <Button variant="outline" size="icon" onClick={() => navigate(-1)}>
                <ArrowLeft className="h-4 w-4" />
            </Button>
            <h1 className="text-2xl font-bold text-destructive">Error</h1>
         </div>
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Error loading payment details: {error instanceof Error ? error.message : 'Payment not found or could not be loaded.'}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
         <div className="flex items-center gap-4">
            <Button variant="outline" size="icon" onClick={() => navigate(-1)} aria-label="Go back">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <h1 className="text-2xl font-bold">
              Payment Details: {paymentData.voucher_number}
            </h1>
             {paymentData.is_cancelled && <Badge variant="destructive">Cancelled</Badge>}
         </div>
         {!paymentData.is_cancelled && (
           <Button
             onClick={() => setShowCancelModal(true)}
             variant="destructive"
           >
             <Ban className="mr-2 h-4 w-4" />
             Cancel Payment
           </Button>
         )}
      </div>

      <Card>
          <CardHeader>
              <CardTitle>Summary</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-4 text-sm">
               {/* Row 1 */}
               <DetailItem icon={CalendarDays} label="Payment Date" value={formatDate(paymentData.transaction_date)} />
               <DetailItem icon={UserCircle} label="Paid To" value={
                 <div className="flex items-center">
                   <span>{paymentData.party?.name || 'N/A'}</span>
                   {paymentData.party && (
                     <RecordLinkIcon 
                       recordType="party" 
                       recordId={paymentData.party.id} 
                       size="sm"
                     />
                   )}
                 </div>
               } />
               <DetailItem icon={Landmark} label="Paid From Account" value={paymentData.paid_from_account?.name || 'N/A'} />

               {/* Row 2 */}
               <DetailItem icon={Hash} label="Payment #" value={paymentData.voucher_number} />
               <DetailItem icon={FileText} label="Reference #" value={paymentData.reference_number || '-'} />
               <DetailItem icon={HandCoins} label="Amount Paid" value={formatCurrency(paymentData.total_amount)} className="font-semibold text-base" />

               {/* Row 3 (Narration) */}
               {paymentData.narration && (
                  <div className="md:col-span-3 mt-2">
                      <p className="text-muted-foreground font-medium">Notes:</p>
                      <p className="whitespace-pre-wrap">{paymentData.narration}</p>
                  </div>
               )}
          </CardContent>
      </Card>

      <Separator />

      {/* Allocations Table */}
      <Card>
          <CardHeader>
              <CardTitle>Bill Allocations</CardTitle>
          </CardHeader>
          <CardContent>
              {isLoadingAllocations ? (
                <div className="flex justify-center items-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-primary mr-2" />
                  <span className="text-muted-foreground">Loading allocations...</span>
                </div>
              ) : allocationsData && allocationsData.length > 0 ? (
                <div className="rounded-md border">
                  <table className="min-w-full divide-y divide-border">
                    <thead>
                      <tr className="bg-muted/50">
                        <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Bill #</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Date</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">Allocated Amount</th>
                      </tr>
                    </thead>
                    <tbody className="bg-card divide-y divide-border">
                      {allocationsData.map((allocation) => (
                        <tr key={allocation.id}>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">
                            <div className="flex items-center">
                              <span>{allocation.invoice_voucher?.voucher_number || `Bill #${allocation.invoice_voucher_id}`}</span>
                              {allocation.invoice_voucher_id && (
                                <RecordLinkIcon 
                                  recordType="purchase-invoice" 
                                  recordId={allocation.invoice_voucher_id} 
                                  size="sm"
                                />
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">
                            {allocation.invoice_voucher?.transaction_date ? 
                              formatDate(allocation.invoice_voucher.transaction_date) : 
                              formatDate(allocation.allocation_date)}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-medium">
                            {formatCurrency(allocation.allocated_amount)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-muted/50">
                        <th className="px-4 py-3 text-left text-sm font-medium" colSpan={2}>Total</th>
                        <th className="px-4 py-3 text-right text-sm font-medium">
                          {formatCurrency(allocationsData.reduce((sum, item) => sum + Number(item.allocated_amount), 0))}
                        </th>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              ) : (
                <p className="text-muted-foreground py-4 text-center">No bill allocations found for this payment.</p>
              )}
          </CardContent>
      </Card>

      {/* Cancel Payment Modal */}
      <CancelPaymentModal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        onConfirm={handleConfirmCancel}
        paymentNumber={paymentData?.voucher_number || ''}
        isLoading={cancelPaymentMutation.isPending}
      />
    </div>
  );
};

// Helper component for consistent detail display
interface DetailItemProps {
    icon: React.ElementType;
    label: string;
    value: React.ReactNode;
    className?: string;
}

const DetailItem: React.FC<DetailItemProps> = ({ icon: Icon, label, value, className }) => (
    <div className={`flex items-start space-x-2 ${className}`}>
        <Icon className="h-4 w-4 mt-0.5 text-muted-foreground flex-shrink-0" aria-hidden="true" />
        <div className="flex-1">
            <p className="text-muted-foreground">{label}</p>
            <p className="font-medium">{value}</p>
        </div>
    </div>
);

export default PaymentsOutDetailPage;